import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Alert,
  AlertTitle
} from '@mui/material';
import { Refresh, Home } from '@mui/icons-material';

/**
 * Composant Error Boundary pour capturer et gérer les erreurs React
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Met à jour l'état pour afficher l'interface d'erreur lors du prochain rendu
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log l'erreur pour le débogage
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Ici, vous pourriez envoyer l'erreur à un service de logging
    // comme Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // Exemple: logErrorToService(error, errorInfo);
    }
  }

  handleReload = () => {
    // Recharger la page
    window.location.reload();
  };

  handleGoHome = () => {
    // Rediriger vers l'accueil
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            {/* Icône d'erreur stylisée */}
            <Box
              sx={{
                fontSize: '4rem',
                mb: 3,
                display: 'inline-block',
                padding: 3,
                borderRadius: '50%',
                backgroundColor: 'error.50',
                color: 'error.main'
              }}
            >
              ⚠️
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
              Oups ! Une erreur s'est produite
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Nous sommes désolés, mais quelque chose s'est mal passé. 
              Notre équipe a été automatiquement notifiée de ce problème.
            </Typography>

            {/* Détails de l'erreur en mode développement */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Alert severity="error" sx={{ mb: 4, textAlign: 'left' }}>
                <AlertTitle>Détails de l'erreur (Mode développement)</AlertTitle>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  <strong>Erreur:</strong> {this.state.error.toString()}
                </Typography>
                {this.state.errorInfo.componentStack && (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    <strong>Stack trace:</strong>
                    <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </Typography>
                )}
              </Alert>
            )}

            {/* Actions pour l'utilisateur */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{ minWidth: 160 }}
              >
                Recharger la page
              </Button>
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={this.handleGoHome}
                sx={{ minWidth: 160 }}
              >
                Retour à l'accueil
              </Button>
            </Box>

            {/* Conseils pour l'utilisateur */}
            <Box sx={{ mt: 6, p: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Que pouvez-vous faire ?
              </Typography>
              <Box sx={{ textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Rechargez la page pour essayer à nouveau
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Vérifiez votre connexion internet
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Videz le cache de votre navigateur
                </Typography>
                <Typography variant="body2">
                  • Si le problème persiste, contactez le support technique
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      );
    }

    // Si pas d'erreur, rendre les children normalement
    return this.props.children;
  }
}

/**
 * Hook personnalisé pour gérer les erreurs dans les composants fonctionnels
 */
export const useErrorHandler = () => {
  const handleError = React.useCallback((error, errorInfo = {}) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo);
    
    // En production, envoyer l'erreur à un service de logging
    if (process.env.NODE_ENV === 'production') {
      // Exemple: logErrorToService(error, errorInfo);
    }
    
    // Vous pourriez aussi déclencher une notification ou un toast
    // showNotification('Une erreur s\'est produite', 'error');
  }, []);

  return { handleError };
};

/**
 * Composant wrapper pour les erreurs asynchrones
 */
export const AsyncErrorBoundary = ({ children, fallback: Fallback }) => {
  const [error, setError] = React.useState(null);

  const resetError = () => setError(null);

  React.useEffect(() => {
    if (error) {
      console.error('AsyncErrorBoundary caught error:', error);
    }
  }, [error]);

  if (error) {
    return Fallback ? <Fallback error={error} resetError={resetError} /> : (
      <Alert severity="error" sx={{ m: 2 }}>
        <AlertTitle>Erreur</AlertTitle>
        Une erreur s'est produite : {error.message}
        <Button onClick={resetError} sx={{ mt: 1 }}>
          Réessayer
        </Button>
      </Alert>
    );
  }

  return (
    <ErrorBoundary>
      {React.cloneElement(children, { onError: setError })}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;